"use client";
/**
 * WM-091 — Global "Send feedback" floating widget.
 *
 * Features:
 *  - Floating button in bottom-right corner
 *  - Modal with category + message form
 *  - Auto page_context from window.location.pathname (no query params)
 *  - Workshop context auto-detected from URL if present
 *  - States: idle / submitting / success / error
 *  - No browser alert()
 *  - Responsive at all breakpoints
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import { api, FeedbackCategory, submitFeedback } from "@/lib/api";

const CATEGORIES: { value: FeedbackCategory; label: string; emoji: string }[] = [
  { value: "BUG", label: "Problemă", emoji: "🐛" },
  { value: "CONFUSING", label: "Nu este clar", emoji: "😕" },
  { value: "FEATURE_REQUEST", label: "Sugestie", emoji: "💡" },
  { value: "OTHER", label: "Întrebare", emoji: "💬" },
];

type WidgetState = "idle" | "open" | "submitting" | "success" | "error";

function extractWorkshopId(pathname: string): number | undefined {
  const match = pathname.match(/\/workshops\/(\d+)/);
  return match ? Number(match[1]) : undefined;
}

export function FeedbackWidget() {
  const pathname = usePathname();
  const [state, setState] = useState<WidgetState>("idle");
  const [category, setCategory] = useState<FeedbackCategory>("BUG");
  const [message, setMessage] = useState("");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  // Close on Escape
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape" && state === "open") {
        setState("idle");
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [state]);

  // Close on outside click
  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (state === "open" && dialogRef.current && !dialogRef.current.contains(e.target as Node)) {
        setState("idle");
      }
    }
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [state]);

  function handleOpen() {
    setMessage("");
    setCategory("BUG");
    setErrorMsg(null);
    setState("open");
  }

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!message.trim()) return;
      setState("submitting");
      setErrorMsg(null);

      // page_context = full URL (href) per P14 spec
      const page_context = typeof window !== "undefined" ? window.location.href : pathname;
      // workshop_id extracted from pathname portion of the URL
      const workshop_id = extractWorkshopId(typeof window !== "undefined" ? window.location.pathname : pathname);

      const resp = await submitFeedback({
        category,
        message: message.trim(),
        page_context,
        ...(workshop_id !== undefined ? { workshop_id } : {}),
      });

      if (!resp.ok) {
        setState("error");
        setErrorMsg(resp.error ?? "Failed to send feedback");
        return;
      }
      setState("success");
      // Auto-close after 3s
      setTimeout(() => setState("idle"), 3000);
    },
    [category, message, pathname]
  );

  return (
    <>
      {/* Floating trigger button
          WM-104 (P4): bottom-4 right-4 keeps the button inside the
          viewport on all mobile widths including 320 px. The icon-only
          label ("hidden sm:inline") avoids horizontal overflow on narrow
          screens while remaining accessible via aria-label. */}
      <button
        onClick={handleOpen}
        className="fixed bottom-4 right-4 z-40 flex items-center gap-2 bg-[#FF7100] hover:bg-[#E06300] text-white text-sm font-medium px-3 py-2 sm:px-4 sm:py-2.5 rounded-full shadow-lg transition-colors focus:outline-none focus:ring-2 focus:ring-[#FF7100] focus:ring-offset-2"
        aria-label="Ajutor feedback"
      >
        <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
        </svg>
        <span className="hidden sm:inline">Ajutor &amp; feedback</span>
      </button>

      {/* Modal overlay */}
      {(state === "open" || state === "submitting" || state === "success" || state === "error") && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center p-4">
          <div
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-label="Send feedback"
            className="bg-white rounded-xl shadow-xl w-full max-w-sm"
          >
            <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-base font-semibold text-[#0D1117]">Ajutor &amp; feedback</h2>
              <button
                onClick={() => setState("idle")}
                className="text-gray-400 hover:text-gray-600 text-xl leading-none"
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className="px-5 py-4">
              {state === "success" ? (
                <div className="text-center py-6 space-y-3">
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-orange-100 rounded-full">
                    <svg className="w-6 h-6 text-[#FF7100]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <p className="text-sm font-medium text-[#0D1117]">Mulțumim! Mesajul tău a fost trimis.</p>
                  <p className="text-xs text-gray-500">Se închide automat…</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Category */}
                  <div className="space-y-1.5">
                    <label className="block text-sm font-medium text-gray-700">
                      Cu ce te putem ajuta?
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      {CATEGORIES.map((cat) => (
                        <button
                          key={cat.value}
                          type="button"
                          onClick={() => setCategory(cat.value)}
                          className={`flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
                            category === cat.value
                              ? "border-[#FF7100] bg-orange-50 text-[#FF7100]"
                              : "border-gray-200 text-gray-600 hover:border-gray-300"
                          }`}
                        >
                          <span>{cat.emoji}</span>
                          <span className="truncate text-xs">{cat.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Message */}
                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-gray-700" htmlFor="feedback-msg">
                      Mesaj <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      id="feedback-msg"
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      required
                      rows={4}
                      maxLength={2000}
                      placeholder="Descrie problema, întrebarea sau sugestia…"
                      className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-[#FF7100] resize-none"
                    />
                    <p className="text-xs text-gray-400 text-right">{message.length}/2000</p>
                  </div>

                  {/* Page context (read-only, shown transparently) */}
                  <p className="text-xs text-gray-400">
                    Pagina:{" "}
                    <span className="font-mono break-all">
                      {typeof window !== "undefined" ? window.location.href : pathname}
                    </span>
                  </p>

                  {state === "error" && errorMsg && (
                    <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
                      {errorMsg}
                    </div>
                  )}

                  <div className="flex gap-3">
                    <button
                      type="submit"
                      disabled={state === "submitting" || !message.trim()}
                      className="flex-1 bg-[#FF7100] hover:bg-[#E06300] disabled:opacity-50 text-white text-sm font-medium px-4 py-2.5 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-[#FF7100] min-h-[44px]"
                    >
                      {state === "submitting" ? "Se trimite…" : "Trimite"}
                    </button>
                    <button
                      type="button"
                      onClick={() => setState("idle")}
                      className="px-4 py-2.5 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-400 min-h-[44px]"
                    >
                      Anulează
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
